import { Compass, Lock, TriangleAlert } from "lucide-react";
import { isRouteErrorResponse, useRouteError } from "react-router";
import { BrandMark } from "../components/BrandMark";
import { Button, ButtonLink } from "../components/Button";
import { useDocumentTitle } from "../components/layout";
import { ApiError } from "../lib/api";

/** A by-id lookup that can't exist: 404, or 400 for an id that isn't even well-formed. */
export function isMissingResource(error: unknown) {
  return error instanceof ApiError && (error.status === 404 || error.status === 400);
}

export function NotFoundPage({ inShell = false, what = "page" }: { inShell?: boolean; what?: string }) {
  useDocumentTitle("Not found");
  const body = (
    <section className="status-page">
      <div className="status-page__icon" aria-hidden="true">
        <Compass size={30} />
      </div>
      <p className="status-page__code">404</p>
      <h1 className="page-header__title" data-page-title tabIndex={-1}>
        We couldn't find that {what}
      </h1>
      <p className="muted">It may have been moved, deleted, or never existed in this household.</p>
      <ButtonLink to="/recipes" emphasis="solid" intent="brand">
        Go to recipes
      </ButtonLink>
    </section>
  );
  if (inShell) return body;
  return (
    <main className="status-shell" id="main">
      <BrandMark />
      {body}
    </main>
  );
}

/** 403: the resource exists but this role cannot use it. */
export function ForbiddenPage({ message = "Only household owners and admins can open this.", backTo = "/settings" }: { message?: string; backTo?: string }) {
  useDocumentTitle("Access denied");
  return (
    <section className="status-page">
      <div className="status-page__icon status-page__icon--lock" aria-hidden="true">
        <Lock size={30} />
      </div>
      <p className="status-page__code">403</p>
      <h1 className="page-header__title" data-page-title tabIndex={-1}>
        You don't have access to this
      </h1>
      <p className="muted">{message}</p>
      <ButtonLink to={backTo} emphasis="solid" intent="brand">
        Go back
      </ButtonLink>
    </section>
  );
}

export function RouteErrorPage() {
  const error = useRouteError();
  useDocumentTitle("Something went wrong");
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;
  if (error instanceof ApiError && error.status === 404) return <NotFoundPage />;
  return (
    <main className="status-shell" id="main">
      <BrandMark />
      <section className="status-page">
        <div className="status-page__icon status-page__icon--error" aria-hidden="true">
          <TriangleAlert size={30} />
        </div>
        <h1 className="page-header__title">Something went wrong</h1>
        <p className="muted">Supperkeep hit an unexpected problem showing this screen. Reloading usually fixes it.</p>
        <div className="row">
          <Button emphasis="solid" intent="brand" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <ButtonLink to="/recipes" emphasis="outline" intent="neutral">
            Go to recipes
          </ButtonLink>
        </div>
      </section>
    </main>
  );
}
